import { faCircleInfo } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';
import { PageContainer } from '@/components/shared/PageContainer';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useTranslator } from '@/hooks/useTranslator';
import { AssessmentBlueprintDialog } from '../../../-components/AssessmentBlueprintDialog';
import { RiskAssessmentsTable } from '../../../-components/RiskAssessmentsTable';
import { useRiskAssessmentsPage } from '../../../-hooks/useRiskAssessmentsPage';

const searchSchema = z.object({
  page: z.number().optional(),
  limit: z.number().optional(),
  sort: z.string().optional(),
  filters: z.record(z.string(), z.array(z.string().nullable())).optional(),
});

export const Route = createFileRoute(
  '/(client)/risk/(pages)/(list)/assessments/',
)({
  validateSearch: searchSchema,
  component: RiskAssessmentsPage,
});

function RiskAssessmentsPage() {
  const { getTranslation: t } = useTranslator({ page: 'riskAssessments' });

  const {
    editBlueprint,
    setEditBlueprint,
    selectedIds,
    setSelectedIds,
    exportTargetId,
    setExportTargetId,
    list,
    createMutation,
    updateMutation,
    archiveMutation,
    exportMutation,
    formulaGroups,
    canCreate,
    canEdit,
    canArchive,
    handleSubmit,
    handleBulkArchive,
    handleView,
    handleExport,
    handleExportConfirm,
  } = useRiskAssessmentsPage();

  return (
    <PageContainer>
      <Alert variant='info' className='mb-4'>
        <FontAwesomeIcon icon={faCircleInfo} className='size-4 shrink-0' />
        <div className='min-w-0'>
          <AlertTitle className='text-sm font-medium'>
            {t('recalculateBanner.title')}
          </AlertTitle>
          <AlertDescription className='text-xs'>
            {t('recalculateBanner.description')}
          </AlertDescription>
        </div>
      </Alert>

      <RiskAssessmentsTable
        rows={list.rows}
        total={list.total}
        isLoading={list.isLoading}
        sorting={list.sorting}
        filters={list.filters}
        pagination={list.pagination}
        onSortingChange={list.onSortingChange}
        onFiltersChange={list.onFiltersChange}
        onPaginationChange={list.onPaginationChange}
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
        hasSelection={Object.values(selectedIds).some(Boolean)}
        onBulkArchive={handleBulkArchive}
        isArchivePending={archiveMutation.isPending}
        canEdit={canEdit}
        canArchive={canArchive}
        canExport={canCreate}
        onView={handleView}
        onEdit={row => setEditBlueprint(row)}
        {...(canCreate
          ? {
              createTrigger: (
                <AssessmentBlueprintDialog
                  onSubmit={handleSubmit}
                  isPending={createMutation.isPending}
                  formulaGroups={formulaGroups}
                />
              ),
            }
          : {})}
        onExport={handleExport}
      />

      {editBlueprint && canEdit && (
        <AssessmentBlueprintDialog
          blueprint={editBlueprint}
          onEditClose={() => setEditBlueprint(null)}
          onSubmit={handleSubmit}
          isPending={updateMutation.isPending}
          formulaGroups={formulaGroups}
        />
      )}

      {/* Export confirmation dialog */}
      <AlertDialog
        open={!!exportTargetId}
        onOpenChange={open => {
          if (!open) setExportTargetId(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('list.export.confirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('list.export.confirmDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className='flex justify-end gap-2 pt-2'>
            <AlertDialogCancel disabled={exportMutation.isPending}>
              {t('dialog.actions.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              variant='default'
              onClick={() => void handleExportConfirm()}
              disabled={exportMutation.isPending}
            >
              {exportMutation.isPending
                ? t('list.export.exporting')
                : t('list.export.confirm')}
            </AlertDialogAction>
          </div>
        </AlertDialogContent>
      </AlertDialog>
    </PageContainer>
  );
}
